import nanoid from 'nanoid';
import axios from 'axios';

var __assign$1 = (undefined && undefined.__assign) || function () {
    __assign$1 = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign$1.apply(this, arguments);
};
/**
 * A service for exchanging messages between the CP app iframe and parent window.
 * Used internally by CommercePlatformService.
 */
var MessageBus = /** @class */ (function () {
    function MessageBus(appIFrame) {
        if (appIFrame === void 0) { appIFrame = window; }
        this.appIFrame = appIFrame;
        this.allowedOrigin = "*";
        this.resolvers = new Map();
        this.commercePlatformWindow = appIFrame.parent;
        appIFrame.addEventListener("message", this.messageHandler.bind(this), false);
    }
    /**
     * Send a PostMessage to the parent.
     */
    MessageBus.prototype.send = function (cmd, body, options) {
        var _this = this;
        if (options === void 0) { options = {}; }
        var payload = __assign$1(__assign$1({}, options), { id: nanoid(), cmd: cmd, body: body || options.body || "" });
        this.commercePlatformWindow.postMessage(JSON.stringify(payload), this.allowedOrigin);
        return new Promise(function (resolve, reject) {
            _this.resolvers.set(payload.id, { resolve: resolve, reject: reject });
        });
    };
    /**
     * Subscribed to messages from the parent.
     */
    MessageBus.prototype.subscribe = function (topicName, onMessage) {
        var _this = this;
        this.resolvers.set(topicName, { resolve: onMessage });
        return function () {
            _this.resolvers.delete(topicName);
        };
    };
    /**
     * Set the allowed origin for posting messages.
     * Should always be done at the earliest opportunity for security's sake.
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage
     */
    MessageBus.prototype.setAllowedOrigin = function (allowedOrigin) {
        this.allowedOrigin = allowedOrigin;
    };
    /**
     * Private handler for the Post Messages coming to this window.
     */
    MessageBus.prototype.messageHandler = function (event) {
        var hasData = typeof event.data === "string";
        var isFromCP = event.source === this.commercePlatformWindow;
        if (hasData && isFromCP) {
            var parsedData = null;
            try {
                parsedData = JSON.parse(event.data);
            }
            catch (err) {
                console.error(err);
            }
            // handle message response
            if (parsedData && this.resolvers.has(parsedData.id)) {
                var resolver = this.resolvers.get(parsedData.id);
                if (parsedData.error && resolver.reject) {
                    resolver.reject(parsedData.error);
                }
                else {
                    resolver.resolve(parsedData.body);
                }
                this.resolvers.delete(parsedData.id);
            }
            // handle subscriptions
            if (parsedData && this.resolvers.has(parsedData.topicName)) {
                var resolver = this.resolvers.get(parsedData.topicName);
                resolver.resolve(parsedData.body);
            }
        }
    };
    return MessageBus;
}());

var __read$1 = (undefined && undefined.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var EnvironmentOptions;
(function (EnvironmentOptions) {
    EnvironmentOptions["test"] = "test";
    EnvironmentOptions["preprod"] = "preprod";
    EnvironmentOptions["prod"] = "prod";
})(EnvironmentOptions || (EnvironmentOptions = {}));
function isGlobalContextObject(globalContext) {
    if (typeof globalContext === "object" && !Array.isArray(globalContext) && globalContext !== null) {
        if (globalContext.env !== undefined
            && globalContext.orgId !== undefined) {
            return true;
        }
    }
    return false;
}
var CommercePlatform = /** @class */ (function () {
    function CommercePlatform(overrides) {
        var _this = this;
        this.messageBus = new MessageBus();
        this.cancelSubscription = null;
        if (overrides === null || overrides === void 0 ? void 0 : overrides.messageBusAllowedOrigin) {
            this.messageBus.setAllowedOrigin(overrides.messageBusAllowedOrigin);
            return;
        }
        void this.getEnvironment().then(function (env) {
            var urlPrefix = env === "prod" ? "" : env + ".";
            // Get domain by 'skipping' over first two items in array
            var _a = __read$1(document.referrer.split("/"), 3), domain = _a[2];
            switch (domain) {
                case "test.workbench.spscommerce.com":
                case "workbench.spscommerce.com":
                    _this.messageBus.setAllowedOrigin("https://" + urlPrefix + "workbench.spscommerce.com");
                    break;
                case "devalpha.commerce.spscommerce.com":
                    _this.messageBus.setAllowedOrigin("https://devalpha.commerce.spscommerce.com");
                    break;
                // Workbench local
                case "localhost:9300":
                    _this.messageBus.setAllowedOrigin("https://localhost:9300");
                    break;
                // Commerce platform local
                case "localhost:9310":
                    _this.messageBus.setAllowedOrigin("https://localhost:9310");
                    break;
                default:
                    _this.messageBus.setAllowedOrigin("https://" + urlPrefix + "commerce.spscommerce.com");
                    break;
            }
        });
    }
    /**
     * Send a message to Commerce Platform with a new page title.
     * This allows the application to update the page title so the
     * browser will reflect the correct title in tabs, bookmarks, or
     * history.
     *
     * @example
     * commercePlatform.setPageTitle('View Users');
     * // resulting title is "SPS FooBar: View Users"
     * @example
     * commercePlatform.setPageTitle(null);
     * // resulting title is "SPS FooBar"
     */
    CommercePlatform.prototype.setPageTitle = function (title) {
        return this.messageBus.send("setPageTitle", { title: title || false });
    };
    /**
     * Get the Global Context from Commerce Platform.
     * This will include query and hash parameters.
     */
    CommercePlatform.prototype.getGlobalContext = function () {
        return this.messageBus.send("getGlobalContext")
            .then(
        // null is part of the GlobalContext contract which can be null if there is no context
        function (c) { return (isGlobalContextObject(c) ? c : null); });
    };
    /**
     * Subscribed to the GlobalContext changes in Commerce Platform.
     * @param onChange callback which will be called with an updated context
     */
    CommercePlatform.prototype.subscribeToGlobalContext = function (onChange) {
        if (typeof this.cancelSubscription === "function")
            this.cancelSubscription();
        this.cancelSubscription = this.messageBus.subscribe("GlobalContextChange", 
        // null is part of the GlobalContext contract which can be null if there is no context
        function (c) { return (isGlobalContextObject(c) ? onChange(c) : onChange(null)); });
        return this.cancelSubscription;
    };
    /**
     * Get the Global Context from Commerce Platform.
     * This will include query and hash parameters.
     */
    CommercePlatform.prototype.setGlobalContext = function (globalContext) {
        return this.messageBus.send("setGlobalContext", globalContext);
    };
    /**
     * Open the Contact Form fodal.
     */
    CommercePlatform.prototype.openContactForm = function (targetReceiver) {
        if (targetReceiver) {
            return this.messageBus.send("openContactForm", targetReceiver);
        }
        return this.messageBus.send("openContactForm");
    };
    /**
     * Send a message to Commerce Platform with an updated state.
     * This is used when updating URL query params so that they
     * appear in the browser address bar.
     */
    CommercePlatform.prototype.updateState = function () {
        return this.messageBus.send("appStateChange", {
            path: window.location.href.split("#")[1],
        });
    };
    /**
     * Show a spinner overlay.
     */
    CommercePlatform.prototype.showSpinner = function () {
        return this.messageBus.send("spinnerShow");
    };
    /**
     * Hide the spinner overlay.
     */
    CommercePlatform.prototype.hideSpinner = function () {
        return this.messageBus.send("spinnerHide");
    };
    /**
     * Show the Commerce Platform nav (blue bar).
     */
    CommercePlatform.prototype.showNavigation = function () {
        return this.messageBus.send("navigationShow");
    };
    /**
     * Hide the Commerce Platform nav (blue bar).
     */
    CommercePlatform.prototype.hideNavigation = function () {
        return this.messageBus.send("navigationHide");
    };
    /**
     * Show an error page. Available options are: 400, 403, 404, 500.
     */
    CommercePlatform.prototype.showErrorPage = function (error) {
        return this.messageBus.send("showErrorPage", error);
    };
    /**
     * Get the current application URL from Commerce Platform.
     */
    CommercePlatform.prototype.getAppBaseUrl = function () {
        return this.messageBus.send("getAppBaseUrl");
    };
    /**
     * Get the environment name from Commerce Platform.
     */
    CommercePlatform.prototype.getEnvironment = function () {
        return this.messageBus.send("getEnvironment");
    };
    /**
     * Get the full URL from Commerce Platform.
     * This will include query and hash parameters.
     */
    CommercePlatform.prototype.getCurrentUrl = function () {
        return this.messageBus.send("getCurrentUrl");
    };
    /**
     * Emit an Intercom event through Commerce Platform's Intercom instance
     * @param event Event object which consists of event name and metadata object
     * @example
     * const event = {
     *      event: 'event-name',
     *      metadata: {
     *         someMeta: 'value''
     *      }
     * }
     * commercePlatform.trackIntercomEvent(event);
     */
    CommercePlatform.prototype.trackIntercomEvent = function (event) {
        return this.messageBus.send("trackIntercomEvent", event);
    };
    /**
     * Show Intercom Messenger. If there is a current chat
     * show that, otherwise, display past chats
     */
    CommercePlatform.prototype.showIntercom = function () {
        return this.messageBus.send("showIntercom");
    };
    /**
     * Create and show new Intercom Chat in Intercom Messenger
     */
    CommercePlatform.prototype.showNewIntercomMessage = function () {
        return this.messageBus.send("newIntercomMessage");
    };
    CommercePlatform.prototype.getAppIconPath = function () {
        return this.messageBus.send("getAppIconPath");
    };
    return CommercePlatform;
}());

var IdentityService = /** @class */ (function () {
    function IdentityService(apiService, environment) {
        this.apiService = apiService;
        this.environment = environment;
    }
    IdentityService.prototype.me = function () {
        return this.apiService.get(this.uri("identity/users/me"));
    };
    IdentityService.prototype.getUserById = function (userId) {
        return this.apiService.get(this.uri("identity/users/" + userId));
    };
    IdentityService.prototype.uri = function (path) {
        return this.environment === "prod"
            ? "https://id.spsc.io/" + path
            : "https://" + this.environment + ".id.spsc.io/" + path;
    };
    return IdentityService;
}());

var __assign = (undefined && undefined.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (undefined && undefined.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
/**
 * A class that makes interacting with SPS APIs easier, by implementing the
 * thing that every data access layer for an SPS API needs: attaching the
 * Identity token authorization header. Your own data access classes can either
 * instantiate this and make calls on it, or extend this.
 */
var SpsApiService = /** @class */ (function () {
    function SpsApiService(token) {
        var _a;
        var foundToken = token;
        if (!foundToken) {
            foundToken = window.localStorage.getItem("access_token");
        }
        if (!foundToken) {
            foundToken = window.sessionStorage.getItem("token");
        }
        if (!foundToken && window.location.search) {
            // the slice removes the leading '?'
            var queryString = window.location.search.slice(1);
            if (queryString) {
                var tokenParam = queryString
                    .split("&")
                    .map(function (param) { return param.split("="); })
                    .find(function (_a) {
                    var _b = __read(_a, 1), key = _b[0];
                    return key === "access_token";
                });
                if (tokenParam) {
                    _a = __read(tokenParam, 2), foundToken = _a[1];
                }
            }
        }
        if (!foundToken) {
            throw new Error("No Identity token was provided and none could be found. "
                + "A token is required to call SPS APIs.");
        }
        this.authorization = { Authorization: "Bearer " + foundToken };
    }
    SpsApiService.prototype.get = function (uri, headers, options) {
        return this.request("get", uri, headers, options);
    };
    SpsApiService.prototype.post = function (uri, data, headers, options) {
        return this.request("post", uri, headers, options, data);
    };
    SpsApiService.prototype.put = function (uri, data, headers, options) {
        return this.request("put", uri, headers, options, data);
    };
    SpsApiService.prototype.patch = function (uri, data, headers, options) {
        return this.request("patch", uri, headers, options, data);
    };
    SpsApiService.prototype.delete = function (uri, headers, options) {
        return this.request("delete", uri, headers, options);
    };
    SpsApiService.prototype.request = function (method, uri, headers, options, data) {
        if (headers === void 0) { headers = {}; }
        if (options === void 0) { options = {}; }
        var patchedOptions = __assign(__assign({}, options), { headers: __assign(__assign({}, headers), this.authorization) });
        return data
            ? axios[method](uri, data, patchedOptions)
            : axios[method](uri, patchedOptions);
    };
    return SpsApiService;
}());

export { CommercePlatform, EnvironmentOptions, IdentityService, MessageBus, SpsApiService, isGlobalContextObject };
